import React, { useState, useEffect } from "react";
import axios from "axios";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../../components/ui/modal";
import { toast } from "react-toastify";
import { EditProfileModalIcon } from "../../components/svg/Icons";

import { CheckCircle, EyeIcon, EyeOffIcon } from "lucide-react";
import {
  cardContainer,
  sectionContainer,
  title,
  infoGrid,
  labelText,
  valueText,
  modalStyles,
  modalTitle,
  modalDescription,
  formStyles,
  inputGroup,
  label,
  input,
  inputDisabled,
  errorMessage,
  closeButton,
  saveButton,
  saveButtonDisabled,
  successModalStyles,
  successModalCloseButton,
  successModalIconContainer,
  successModalHeading,
  successModalButton,
} from "../../styles/components/UserProfile/UserInfoCard.styles";
import { UserData } from "../UserProfiles";

interface UserInfoCardProps {
  userData: UserData | null;
  setUserData: React.Dispatch<React.SetStateAction<UserData | null>>;
}

const UserInfoCard: React.FC<UserInfoCardProps> = ({
  userData,
  setUserData,
}) => {
  const { isOpen, openModal, closeModal } = useModal();
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [editData, setEditData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile_number: "",
    roles: [] as string[],
    password: "",
    confirm_password: "",
  });
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const userId = sessionStorage.getItem("user_id");

  useEffect(() => {
    if (userData) {
      setEditData({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        mobile_number: userData.mobile_number || "",
        roles: userData.roles || [],
        password: "",
        confirm_password: "",
      });
      setProfileImageUrl(userData.profile_image || null);
    }
  }, [userData]);

  useEffect(() => {
    if (successModalOpen) {
      const timer = setTimeout(() => {
        setSuccessModalOpen(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [successModalOpen]);

  const handleEditClick = () => {
    if (userData) {
      setEditData({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        mobile_number: userData.mobile_number || "",
        roles: userData.roles || [],
        password: "",
        confirm_password: "",
      });
      setProfileImageUrl(userData.profile_image || null);
      setProfileImage(null);
    }
    openModal();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
    if (name === "mobile_number") {
      const formattedValue = value.replace(/[^0-9+]/g, "").slice(0, 10);
      setEditData((prev) => ({ ...prev, [name]: formattedValue }));
    } else {
      setEditData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImage(file);
      setProfileImageUrl(URL.createObjectURL(file));
      setFormErrors((prev) => ({ ...prev, profile_image: "" }));
    }
  };

  const handleUpdateUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setFormErrors({});

    const formData = new FormData();
    formData.append("user_id", userId || "");
    formData.append("first_name", editData.first_name);
    formData.append("last_name", editData.last_name);
    formData.append("email", editData.email);
    formData.append("mobile_number", editData.mobile_number);
    if (editData.password) {
      formData.append("password", editData.password);
    }
    if (editData.confirm_password) {
      formData.append("confirm_password", editData.confirm_password);
    }
    if (profileImage) {
      formData.append("profile_image", profileImage);
    } else if (userData?.profile_image) {
      formData.append("existing_profile_image", userData.profile_image); // ← add existing one if not changed
    }

    // Log form data before submission
    console.log("Submitting form with data:", {
      first_name: editData.first_name,
      last_name: editData.last_name,
      email: editData.email,
      mobile_number: editData.mobile_number,
      password: editData.password,
      confirm_password: editData.confirm_password,
      profile_image: profileImage ? profileImage.name : null,
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/profile/update`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === 200) {
        // Log updated data from server
        console.log("Updated user data from server:", response.data.data);

        const updatedData = response.data.data;
        setUserData((prevData) => {
          const baseData: UserData = prevData || {
            first_name: "",
            last_name: "",
            email: "",
            mobile_number: "",
            roles: [],
            profile_image: "",
          };
          return {
            ...baseData,
            first_name: editData.first_name || baseData.first_name,
            last_name: editData.last_name || baseData.last_name,
            email: editData.email || baseData.email,
            mobile_number: editData.mobile_number || baseData.mobile_number,
            roles: updatedData?.roles || baseData.roles,
            profile_image: updatedData?.profile_image || baseData.profile_image,
          };
        });
        setSuccessMessage(response.data.message || "Profile updated successfully!");
        setSuccessModalOpen(true);
        closeModal();
        window.dispatchEvent(new Event("profileUpdated"));
      } else if (response.data.status === 422) {
        const apiErrors = response.data.message;
        if (apiErrors.mobile_number) {
          toast.error(apiErrors.mobile_number, {
            position: "top-right",
            autoClose: 1500,
          });
        }
        setFormErrors(apiErrors || {});
      } else if (response.data.status === 500) {
        toast.error(response.data.message || "Internal Server Error", {
          position: "top-right",
          autoClose: 1500,
        });
      } else {
        toast.error("Unexpected server response. Please try again.", {
          position: "top-right",
          autoClose: 2000,
        });
      }
    } catch (error: any) {
      console.error("API Error:", error.response?.data);
      toast.error("Something went wrong while updating your profile.", {
        position: "top-right",
        autoClose: 2000,
      });
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className={cardContainer}>
      <div className={sectionContainer}>
        <div>
          <h4 className={title}>Personal Information</h4>
          <div className={infoGrid}>
            {(["first_name", "last_name"] as const).map((field) => (
              <div key={field}>
                <p className={labelText}>
                  {field
                    .replace("_", " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                </p>
                <p className={valueText}>{userData?.[field] || "N/A"}</p>
              </div>
            ))}
            <div>
              <p className={labelText}>Roles</p>
              <p className={valueText}>
                {userData?.roles?.join(", ") || "N/A"}
              </p>
            </div>
            <div>
              <p className={labelText}>Mobile Number</p>
              <p className={valueText}>{userData?.mobile_number || "N/A"}</p>
            </div>
            <div>
              <p className={labelText}>Email</p>
              <p className={valueText}>{userData?.email || "N/A"}</p>
            </div>
          </div>
        </div>
        <button
          onClick={handleEditClick}
          className="mt-4 xl:mt-0 flex justify-center items-center gap-2 px-4 py-2 text-sm bg-[#2f6aa7] dark:bg-[#2f6aa7] text-white rounded-lg hover:bg-[#25578a] dark:hover:bg-[#25578a]"
        >
          <EditProfileModalIcon />
          Edit
        </button>
      </div>
      <Modal isOpen={isOpen} onClose={closeModal} className={modalStyles}>
        <h4 className={modalTitle}>Personal Information</h4>
        <p className={modalDescription}>
          Update your details to keep your profile up-to-date.
        </p>
        <form onSubmit={handleUpdateUser} className={formStyles}>
          <div className={inputGroup}>
            {["first_name", "last_name"].map((field) => (
              <div key={field} className="relative">
                <label className={label}>
                  {field
                    .replace("_", " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                  {["first_name", "last_name"].includes(field) && (
                    <span className="text-error-500 dark:text-red-400">
                      {" "}
                      *{" "}
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  name={field}
                  value={editData[field as keyof typeof editData] || ""}
                  onChange={handleInputChange}
                  className={`${input} ${formErrors[field]
                      ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                      : ""
                    }`}
                />
                {formErrors[field] && (
                  <p className={errorMessage}>{formErrors[field]}</p>
                )}
              </div>
            ))}
          </div>
          <div className={inputGroup}>
            {["mobile_number", "email"].map((field) => (
              <div key={field} className="relative">
                <label className={label}>
                  {field
                    .replace("_", " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                  {field === "mobile_number" && (
                    <span className="text-error-500 dark:text-red-400">
                      {" "}
                      *{" "}
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  name={field}
                  value={editData[field as keyof typeof editData] || ""}
                  onChange={
                    field === "mobile_number"
                      ? (e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                        const event = {
                          target: { name: field, value },
                        } as React.ChangeEvent<HTMLInputElement>;
                        handleInputChange(event);
                      }
                      : handleInputChange
                  }
                  disabled={field === "email"}
                  className={`${field === "email" ? inputDisabled : input} ${formErrors[field]
                      ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                      : ""
                    }`}
                />
                {formErrors[field] && (
                  <p className={errorMessage}>{formErrors[field]}</p>
                )}
              </div>
            ))}
          </div>
          <div className={inputGroup}>
            <div className="relative">
              <label className={label}>Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={editData.password || ""}
                  onChange={handleInputChange}
                  className={`${input} pr-10 ${formErrors.password
                      ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                      : ""
                    }`}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500 dark:text-gray-500"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeIcon size={20} />
                  ) : (
                    <EyeOffIcon size={20} />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p className={errorMessage}>{formErrors.password}</p>
              )}
            </div>
            <div className="relative">
              <label className={label}>Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirm_password"
                  value={editData.confirm_password || ""}
                  onChange={handleInputChange}
                  className={`${input} pr-10 ${formErrors.confirm_password
                      ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                      : ""
                    }`}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500 dark:text-gray-500"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeIcon size={20} />
                  ) : (
                    <EyeOffIcon size={20} />
                  )}
                </button>
              </div>
              {formErrors.confirm_password && (
                <p className={errorMessage}>{formErrors.confirm_password}</p>
              )}
            </div>
          </div>
          <div className={inputGroup}>
            <div className="relative">
              <label className={label}>Profile Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleProfileImageChange}
                className={`${input} ${formErrors.profile_image
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                  }`}
              />
              {formErrors.profile_image && (
                <p className={errorMessage}>{formErrors.profile_image}</p>
              )}
            </div>
            <div className="flex-1">
              {profileImageUrl && (
                <div>
                  <p className={label}>Image Preview</p>
                  <img
                    src={profileImageUrl}
                    alt="Profile Preview"
                    className="w-24 h-24 object-contain rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 p-2"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-6 mt-4 items-center">

            <div className="flex-1 flex gap-4 justify-center sm:justify-end">
              <button
                type="submit"
                disabled={saving}
                className={`${saveButton} ${saving ? saveButtonDisabled : ""}`}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={closeModal}
                className={closeButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </Modal>
      <Modal
        isOpen={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        className={successModalStyles}
      >
        <button
          onClick={() => setSuccessModalOpen(false)}
          className={successModalCloseButton}
        />
        <div className="flex justify-center">
          <div className={successModalIconContainer}>
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className={successModalHeading}>{successMessage}</h4>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setSuccessModalOpen(false)}
            className={successModalButton}
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default UserInfoCard;

